import { useState } from 'react';
import { Link } from 'react-router-dom';

const PHONE = '07760 881212';

const services = [
  'House Rewiring',
  'Fuse Board Upgrade',
  'EICR Certificate',
  'New Sockets / Lighting',
  'EV Charger Installation',
  'Outdoor / Garden Electrics',
  'Bathroom Electrics',
  'Consumer Unit Replacement',
  'Fault Finding',
  'Other',
];

export default function HeroSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => { body.append(key, value.toString()); });
    try {
      await fetch('https://readdy.ai/api/form/d77rc2adqn9mib2tqcn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1cf64004b6cb1fa81a7c386c50958670.jpg"
          alt="Professional electrician working in Worksop"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-800/85 to-navy-900/70"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left — Headline + trust */}
          <div>
            <h1 className="font-heading font-extrabold text-white leading-none mb-5" style={{ fontSize: 'clamp(2.6rem, 5.5vw, 4.5rem)', lineHeight: 1.05 }}>
              Electrician <span className="text-gold-400">Worksop</span>
              <br />
              Reliable Local<br />Electrical Services
            </h1>

            <p className="font-body text-gold-100/80 text-lg leading-relaxed mb-8 max-w-lg">
              Fast response, honest pricing, and tidy work. Serving Worksop, Retford, Carlton in Lindrick, and Shireoaks.{' '}
              <strong className="text-white">Fully insured &amp; NAPIT registered.</strong>
            </p>

            {/* Call button */}
            <a
              href={`tel:${PHONE.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-3 bg-gold-400 hover:bg-gold-300 text-navy-800 font-body font-bold text-lg px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap mb-8"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-phone-fill text-xl"></i>
              </span>
              Call Now — {PHONE}
            </a>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: 'ri-shield-check-fill', label: 'Fully Insured' },
                { icon: 'ri-award-fill', label: 'NAPIT Registered' },
                { icon: 'ri-user-star-fill', label: 'Part P Certified' },
                { icon: 'ri-star-fill', label: '5-Star Rated' },
              ].map((badge) => (
                <div key={badge.label} className="flex items-center gap-2 bg-navy-800/50 backdrop-blur-sm border border-gold-400/30 text-white text-sm font-semibold px-3 py-2 rounded-md font-body">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-gold-400 text-sm`}></i>
                  </span>
                  {badge.label}
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-body font-semibold text-sm transition-colors cursor-pointer"
              >
                View all our services
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>

          {/* Right — Quote Form */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl border-2 border-gold-400">
            {/* Form header */}
            <div className="bg-gold-400 px-6 py-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-navy-800 rounded-full">
                <i className="ri-file-list-3-fill text-gold-400 text-lg"></i>
              </div>
              <div>
                <p className="font-heading font-extrabold text-navy-800 text-lg leading-none">Get a Free Quote</p>
                <p className="font-body text-navy-700 text-xs mt-0.5">We&apos;ll get back to you the same day</p>
              </div>
              <div className="ml-auto text-right hidden sm:block">
                <p className="font-body text-navy-700 text-xs font-semibold">No call-out charge</p>
                <p className="font-body text-navy-700 text-xs">No obligation</p>
              </div>
            </div>

            {submitted ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                </div>
                <h3 className="font-heading font-extrabold text-navy-800 text-xl mb-2">Quote Request Sent!</h3>
                <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">
                  Thanks — we&apos;ll review your details and get back to you the same day with a price.
                </p>
                <a
                  href={`tel:${PHONE.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-navy-800 font-bold text-sm px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill"></i>
                  </span>
                  Or call us: {PHONE}
                </a>
              </div>
            ) : (
              <form
                data-readdy-form
                id="home-quote-form"
                action="https://readdy.ai/api/form/d77rc2adqn9mib2tqcn0"
                method="POST"
                onSubmit={handleSubmit}
                className="px-6 py-5 space-y-3"
              >
                {/* Name + Phone row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Your Name <span className="text-red-500">*</span></label>
                    <input
                      name="name"
                      type="text"
                      placeholder="e.g. John Smith"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 placeholder-gray-400 focus:outline-none focus:border-gold-400 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Phone Number <span className="text-red-500">*</span></label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="e.g. 07700 900000"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 placeholder-gray-400 focus:outline-none focus:border-gold-400 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Email Address</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="e.g. john@email.com"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 placeholder-gray-400 focus:outline-none focus:border-gold-400 bg-gray-50"
                  />
                </div>

                {/* Service type */}
                <div>
                  <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Type of Work Required <span className="text-red-500">*</span></label>
                  <select
                    name="service"
                    required
                    defaultValue=""
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 focus:outline-none focus:border-gold-400 bg-gray-50"
                  >
                    <option value="" disabled>Select a service...</option>
                    {services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Postcode + Property type row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Postcode <span className="text-red-500">*</span></label>
                    <input
                      name="postcode"
                      type="text"
                      placeholder="e.g. S80 1AB"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 placeholder-gray-400 focus:outline-none focus:border-gold-400 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Property Type</label>
                    <select
                      name="property_type"
                      defaultValue=""
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 focus:outline-none focus:border-gold-400 bg-gray-50"
                    >
                      <option value="" disabled>Select...</option>
                      <option value="House">House</option>
                      <option value="Flat / Apartment">Flat / Apartment</option>
                      <option value="Bungalow">Bungalow</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Job description */}
                <div>
                  <label className="block font-body text-xs font-semibold text-navy-800 mb-1">Brief Description of the Job <span className="text-red-500">*</span></label>
                  <textarea
                    name="message"
                    placeholder="Tell us a bit about what you need — e.g. 'Full rewire on a 3-bed semi, wiring is original 1970s rubber cable'"
                    rows={3}
                    required
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-body text-navy-800 placeholder-gray-400 focus:outline-none focus:border-gold-400 bg-gray-50 resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-navy-800 hover:bg-navy-700 disabled:opacity-60 text-gold-400 font-heading font-bold text-base py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-send-plane-fill"></i>
                      </span>
                      Request Free Quote
                    </>
                  )}
                </button>

                <p className="font-body text-center text-xs text-gray-400">
                  Prefer to call?{' '}
                  <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="text-gold-500 font-semibold hover:underline cursor-pointer">
                    {PHONE}
                  </a>
                </p>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
